import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/formatApiError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { CodeInput } from "@/components/auth/TwoFactorStep";

export const TwoFactorCard = () => {
  const { user, refreshSession } = useAuth();
  const enabled = !!user?.two_factor_enabled;
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState("email");
  const channelLabel = user?.two_factor_channel === "sms" ? "par SMS" : "par email";

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (err) { toast.error(formatApiError(err, "Erreur")); } finally { setBusy(false); }
  };

  const start = () => run(async () => {
    const { data } = await api.startEnableTwoFactor(password, channel);
    setChallenge(data);
    setPassword("");
    toast.success(`Code envoyé à ${data.email_hint}`);
  });

  const confirm = () => run(async () => {
    await api.confirmEnableTwoFactor(challenge.challenge_id, code);
    refreshSession({ user: { two_factor_enabled: true, two_factor_channel: challenge.channel || "email" } });
    setChallenge(null);
    setCode("");
    toast.success("Validation en deux étapes activée");
  });

  const disable = () => run(async () => {
    await api.disableTwoFactor(password);
    refreshSession({ user: { two_factor_enabled: false } });
    setPassword("");
    toast.success("Validation en deux étapes désactivée");
  });

  return (
    <Card data-testid="two-factor-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-indigo-600" /> Validation en deux étapes
          <Badge variant="outline" className={enabled ? "border-emerald-300 bg-emerald-50 text-emerald-800" : ""} data-testid="two-factor-status">
            {enabled ? "Activée" : "Désactivée"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          À chaque connexion, un code à 6 chiffres vous est envoyé en plus du mot de passe
          {enabled ? ` (actuellement ${channelLabel}).` : "."}
        </p>
        {!enabled && !challenge && (
          <div className="flex gap-2" data-testid="two-factor-channel">
            <Button type="button" size="sm" variant={channel === "email" ? "default" : "outline"}
              onClick={() => setChannel("email")} data-testid="two-factor-channel-email">Par email</Button>
            <Button type="button" size="sm" variant={channel === "sms" ? "default" : "outline"}
              onClick={() => setChannel("sms")} disabled={!user?.phone} data-testid="two-factor-channel-sms"
              title={user?.phone ? "" : "Ajoutez d'abord votre numéro de mobile"}>Par SMS</Button>
          </div>
        )}
        {challenge ? (
          <div className="space-y-3">
            <Label>Code reçu à {challenge.email_hint}</Label>
            <CodeInput value={code} onChange={setCode} testId="two-factor-enable-code-input" />
            <div className="flex gap-2">
              <Button onClick={confirm} disabled={busy || code.length !== 6} data-testid="two-factor-enable-confirm-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Confirmer l'activation
              </Button>
              <Button variant="outline" onClick={() => setChallenge(null)} data-testid="two-factor-enable-cancel-btn">Annuler</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="tf-pwd">Mot de passe actuel *</Label>
              <Input id="tf-pwd" type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="two-factor-password-input" />
            </div>
            {enabled ? (
              <Button variant="outline" onClick={disable} disabled={busy || !password} data-testid="two-factor-disable-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldOff className="h-4 w-4 mr-2" />} Désactiver
              </Button>
            ) : (
              <Button onClick={start} disabled={busy || !password} data-testid="two-factor-enable-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />} Activer
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
